import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { SyntheticEvent, useState, useRef } from "react";
import { useSelector } from "react-redux";
import * as driverActivitiesActions from "../../../ducks/app/driver-activities";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import { DateTime } from "luxon";
import { useAppDispatch } from "../../../redux-store";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import {
  selectStopVolumesFromActivities,
} from "../../../ducks/data/route-with-stops/selectors";
import { StopNew } from "../../../ducks/data/route-with-stops/types";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import useFeature from "../../../hooks/useFeature";
import Decimal from "decimal.js-light";
import { useParams } from "../hooks";
import LoadingButton from "../../../components/LoadingButton";
import { parseTrailerIdParam } from "../../../routes";

const AddCubicMetersModal = (props: { stop: StopNew; onClose: () => void }) => {
  const { stop, onClose } = props;

  const { t } = useTranslation(["stopPage", "components"]);
  const [cubicMeters, setCubicMeters] = useState("");
  const [loading, setLoading] = useState(false);
  const [volumeIdForEdit, setVolumeIdForEdit] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const volumes = useSelector(selectStopVolumesFromActivities(stop.id));
  const inputRef = useRef<HTMLInputElement>(null);
  const params = useParams();
  const trailerId = (params.truckId as string) || "";
  const routeId = (params.routeId as string) || "";
  const unpoweredTrailerId = parseTrailerIdParam(params.trailerId as string);

  const onSaveClick = async () => {
    setLoading(true);
    try {
      await dispatch(
        driverActivitiesActions.editVolume({
          routeId,
          id: volumeIdForEdit!,
          cubicMeters: new Decimal(cubicMeters),
          cargoId: stop.cargoId,
          stopId: stop.id,
          truckId: trailerId,
          unpoweredTrailerId,
        })
      ).unwrap();
      setVolumeIdForEdit(null);
      setCubicMeters("");
      onClose();
    } catch {
      // Already reported and shown by the thunk; keep the modal open.
    } finally {
      setLoading(false);
    }
  };

  const onEditClick = async (volumeId: string) => {
    const volume = volumes.find((v) => v.id === volumeId);
    if (volume) {
      setVolumeIdForEdit(volumeId);
      setCubicMeters(volume.cubicMeters.toString());
      inputRef.current?.focus();
    }
  };

  const onSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (volumeIdForEdit) {
      onSaveClick();
    } else {
      onAddClick();
    }
  };

  const onAddClick = async () => {
    setLoading(true);
    try {
      await dispatch(
        driverActivitiesActions.addVolume({
          routeId,
          cubicMeters: new Decimal(cubicMeters),
          cargoId: stop.cargoId,
          stopId: stop.id,
          truckId: trailerId,
          unpoweredTrailerId,
        })
      ).unwrap();
      onClose();
    } catch {
      // Already reported and shown by the thunk; keep the modal open.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <div className="px-8 py-4">
        <Typography variant="h6" component="h2" pt={2}>
          {t("cubicMeters.heading")}
        </Typography>
        <Typography variant="body1" component="div">
          <p>
            {t("cubicMeters.plannedLabel") + " "}
            {stop.cubicMeters || t("cubicMeters.unspecified")}
          </p>
          <p>
            {t("cubicMeters.actualLabel") + " "}
            {stop.measuredCubicMeters || t("cubicMeters.unspecified")}
          </p>
        </Typography>
        <List>
          {volumes.map((v, i) => (
            <VolumeListItem
              key={i}
              v={{ ...v, cubicMeters: new Decimal(v.cubicMeters) }}
              routeId={routeId}
              cargoId={stop.cargoId}
              stopId={stop.id}
              trailerId={trailerId}
              unpoweredTrailerId={unpoweredTrailerId}
              onEditClick={onEditClick}
            />
          ))}
        </List>
        <form onSubmit={onSubmit} className="pt-4">
          <Grid container spacing={2} direction="column">
            <Grid columns={12}>
              <TextField
                autoFocus
                fullWidth
                type="number"
                inputRef={inputRef}
                value={cubicMeters}
                label={t("stopPage:cubicMeters.textFieldLabel")}
                placeholder="100"
                onChange={(e) => {
                  setCubicMeters(e.target.value);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {t("stopPage:cubicMeters.textFieldAdornment")}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid columns={12}>
              <div className="flex gap-2 justify-between w-100 mt-4">
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={onClose}
                >
                  {t("stopPage:cubicMeters.backButtonLabel")}
                </Button>
                <LoadingButton
                  loading={loading}
                  type="submit"
                  fullWidth
                  size="large"
                  variant="contained"
                >
                  <span>
                    {volumeIdForEdit
                      ? t("components:buttonLabels.save")
                      : t("stopPage:cubicMeters.addButtonLabel")}
                  </span>
                </LoadingButton>
              </div>
            </Grid>
          </Grid>
        </form>
      </div>
    </Dialog>
  );
};

const VolumeListItem = (props: {
  v: { createdAt: string; cubicMeters: Decimal; id: string };
  cargoId: string;
  stopId: string;
  routeId: string;
  trailerId: string;
  unpoweredTrailerId: string | null;
  onEditClick: (volumeId: string) => void;
}) => {
  const { v, cargoId, stopId, routeId, trailerId, unpoweredTrailerId, onEditClick } = props;
  const { t } = useTranslation(["stopPage", "components"]);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const dispatch = useAppDispatch();
  const isEditDeleteEnabled = useFeature("driver-app-edit-delete-volumes");

  const onConfirmDeleteClick = async () => {
    setRequestInProgress(true);
    dispatch(
      driverActivitiesActions.deleteVolume({
        id: v.id,
        cargoId,
        routeId,
        stopId,
        truckId: trailerId,
        unpoweredTrailerId,
      })
    )
      .unwrap()
      .then(() => setShowConfirmDeleteDialog(false))
      .catch(() => {
        // Already reported and shown by the thunk.
      })
      .finally(() => setRequestInProgress(false));
  };

  return (
    <>
      <Dialog
        open={showConfirmDeleteDialog}
        onClose={() => setShowConfirmDeleteDialog(false)}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2" pt={2}>
            {t("stopPage:cubicMeters.deleteConfirmTitle")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" mt={2}>
            {t("stopPage:cubicMeters.deleteConfirmMessage")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <div className="flex gap-2 justify-between w-100 mt-4">
            <Button
              variant="outlined"
              size="large"
              fullWidth
              disabled={requestInProgress}
              onClick={() => setShowConfirmDeleteDialog(false)}
            >
              {t("components:buttonLabels.cancel")}
            </Button>
            <LoadingButton
              type="button"
              fullWidth
              size="large"
              variant="contained"
              color="error"
              disabled={requestInProgress}
              onClick={onConfirmDeleteClick}
            >
              {t("components:buttonLabels.delete")}
            </LoadingButton>
          </div>
        </DialogActions>
      </Dialog>
      <ListItem sx={{ pl: 0 }} key={v.createdAt} disableGutters divider>
        <div className="flex flex-col justify-between w-full">
          <div className="flex justify-between flex-grow items-center">
            <div style={{ color: "var(--gray-500)", fontSize: "0.9rem" }}>
              {DateTime.fromISO(v.createdAt).toLocaleString(
                DateTime.DATETIME_SHORT
              )}
            </div>
            {isEditDeleteEnabled && (
              <div className="flex items-center">
                <IconButton onClick={() => onEditClick(v.id)}>
                  <EditIcon style={{ color: "var(--gray-500)" }} />
                </IconButton>
                <IconButton
                  style={{ paddingRight: 0 }}
                  onClick={() => setShowConfirmDeleteDialog(true)}
                >
                  <DeleteOutlinedIcon style={{ color: "red" }} />
                </IconButton>
              </div>
            )}
          </div>
          <ListItemText primary={`${v.cubicMeters} m³`} />
        </div>
      </ListItem>
    </>
  );
};

export default AddCubicMetersModal;
